'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { Feature4 } from '@/components/sections/feature4';
import {
  BubbleAttachment,
  BubbleQuickReplies,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'Channels',
    title: "DMs that don't keep customers waiting",
    subtitleA: 'Reply to Instagram, Messenger and X DMs in seconds.',
    subtitleB: '24/7 — with the same brand voice every time.',
    insight: 'Damaged item · order #4521 — refund eligible',
    optFullRefund: 'Full refund',
    optReplaceIt: 'Replace it',
    optTalkHuman: 'Talk to a human',
  },
  ar: {
    eyebrow: 'القنوات',
    title: 'رسائل خاصة ما تخلّي العميل ينتظر',
    subtitleA: 'رد على الرسائل الخاصة في Instagram وMessenger وX بثواني.',
    subtitleB: '٢٤/٧ — وبنفس نبرة براندك، كل مرة.',
    // ⁦…⁩ = LRI/PDI. Without the isolate RTL bidi renders "#4521" as "4521#".
    insight: 'منتج تالف · طلب ⁦#4521⁩ — قابل للاسترجاع',
    optFullRefund: 'استرجاع كامل',
    optReplaceIt: 'استبدله',
    optTalkHuman: 'كلّم موظف',
  },
} as const;

export default function SocialPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitleA}
            <br />
            {c.subtitleB}
          </>
        }
        videoSrc="/home/features/social.mp4"
        bubblePosition="bottom-left"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleAttachment
                fileName="dm-screenshot.jpg"
                fileType="IMG"
                insight={c.insight}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleQuickReplies
                options={[c.optFullRefund, c.optReplaceIt, c.optTalkHuman]}
                selected={c.optFullRefund}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['Social Media AI Features']}
        subtitle={t.channels['Support customers where they discover you']}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['Instagram DM Automation'],
            description:
              t.channels[
                'Agent 5 responds to Instagram Direct Messages instantly. Handles story mentions, comment replies, and customer inquiries without leaving Instagram.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Facebook Messenger Support'],
            description:
              t.channels[
                'Instant AI responses on Messenger. Handle support and inquiries automatically with rich media and quick reply buttons.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Unified Social Inbox'],
            description:
              t.channels[
                'See Instagram and Messenger messages in one place. Manage all social conversations from a single dashboard.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Rich Visual Responses'],
            description:
              t.channels[
                'Send images, videos, GIFs, and carousels in replies. Show products, guides, or visual answers that engage.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Story & Comment Monitoring'],
            description:
              t.channels[
                'Agent 5 monitors post comments and responds automatically. Can send private message follow-ups for complex questions.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-5.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Seamless Human Handoffs'],
            description:
              t.channels[
                'Transfer conversations to agents without disruption. Same thread, full context, invisible to customers.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-6.svg',
            imagePosition: 'left',
          },
        ]}
      />

      <Feature4
        heading={
          t.channels[
            'Social AI that handles Instagram and Messenger without changing your setup'
          ]
        }
      />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels['Find answers to common questions about Social support.']
        }
        items={[
          {
            question: t.channels['Does Agent 5 work with Instagram DMs?'],
            answer:
              t.channels[
                'Yes. Agent 5 responds to Instagram Direct Messages automatically, handling customer inquiries and support requests.'
              ],
          },
          {
            question: t.channels['Can it reply to Facebook Messenger?'],
            answer:
              t.channels[
                'Yes. Agent 5 handles Facebook Messenger conversations, page messages, and customer inquiries with instant AI responses.'
              ],
          },
          {
            question: t.channels['Can I see all social messages in one place?'],
            answer:
              t.channels[
                'Yes. The unified inbox shows Instagram and Messenger conversations together. Manage all social support from one dashboard.'
              ],
          },
          {
            question: t.channels['Can Agent 5 respond to comments on posts?'],
            answer:
              t.channels[
                'Yes. Agent 5 can monitor post comments and respond automatically. It can also send private message follow-ups for complex questions.'
              ],
          },
          {
            question: t.channels['Can it send images and videos?'],
            answer:
              t.channels[
                'Yes. Agent 5 can send images, videos, GIFs, and product carousels (on Messenger) to provide visual answers and support.'
              ],
          },
          {
            question:
              t.channels['How do social media handoffs to human agents work?'],
            answer:
              t.channels[
                'The same conversation thread continues. Human agents see full history and can reply directly in Instagram or Messenger.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
